'use client';

import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWallet } from './use-wallet';

/**
 * Tracks + persists whether the lobby's "⌘/Ctrl + drag" multi-select hint
 * has been dismissed. Dismissal is one-way and persisted in two places, per
 * product spec:
 *   - **localStorage** — "for this browser" (covers signed-out + instant).
 *   - **DB by address** — so it stays hidden on any browser they sign in on.
 *
 * `dismissed` is the OR of both. `dismiss()` writes both (the DB write is
 * best-effort and only fires for a linked/authed wallet).
 */
const LS_KEY = 'lobby:select-prompt-dismissed';

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  try {
    const privyToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('privy-token='))
      ?.split('=')[1];
    if (privyToken) headers['Authorization'] = `Bearer ${privyToken}`;
  } catch {
    // ignore — fall back to localStorage-only
  }
  return headers;
}

function readLocal(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLocal(): void {
  try {
    window.localStorage.setItem(LS_KEY, '1');
  } catch {
    // ignore (private mode / storage disabled)
  }
}

export function useSelectPromptDismissed(): {
  dismissed: boolean;
  dismiss: () => void;
} {
  const { address, isConnected, isAddressLinked } = useWallet();
  const queryClient = useQueryClient();

  // Read localStorage after mount (lazy) to avoid an SSR hydration mismatch.
  const [localDismissed, setLocalDismissed] = useState(false);
  useEffect(() => {
    setLocalDismissed(readLocal());
  }, []);

  const dbQuery = useQuery({
    queryKey: ['select-prompt-dismissed', address?.toLowerCase() ?? null],
    queryFn: async (): Promise<boolean> => {
      if (!address) return false;
      const headers = await getAuthHeaders();
      const res = await fetch(
        `/api/user/ui-flags?address=${encodeURIComponent(address)}`,
        { headers },
      );
      if (!res.ok) return false;
      const json = await res.json();
      return !!json.flags?.select_prompt_dismissed;
    },
    // Same linked-wallet gate as the other per-address reads — avoids 403s
    // for a wagmi account Privy never signed against.
    enabled: isConnected && !!address && isAddressLinked,
    staleTime: 5 * 60_000,
  });

  const dbDismissed = dbQuery.data === true;

  // A DB-confirmed dismissal mirrors back into localStorage so the next load
  // (even signed-out) hides the hint without waiting on the fetch.
  useEffect(() => {
    if (dbDismissed && !localDismissed) {
      writeLocal();
      setLocalDismissed(true);
    }
  }, [dbDismissed, localDismissed]);

  const dismissed = localDismissed || dbDismissed;

  const dismiss = useCallback(() => {
    if (!readLocal()) writeLocal();
    setLocalDismissed(true);
    if (address && isConnected && isAddressLinked) {
      getAuthHeaders()
        .then((headers) =>
          fetch('/api/user/ui-flags', {
            method: 'POST',
            headers,
            body: JSON.stringify({ address }),
          }),
        )
        .then(() => {
          queryClient.setQueryData(
            ['select-prompt-dismissed', address.toLowerCase()],
            true,
          );
        })
        .catch(() => {
          // best-effort — localStorage already covers this browser
        });
    }
  }, [address, isConnected, isAddressLinked, queryClient]);

  return { dismissed, dismiss };
}
